import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FileText, RefreshCw, Eye, Edit2, Trash2, 
  Image, ExternalLink, ChevronDown, ChevronUp, Star, Zap, Sparkles, Upload
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import * as api from '../../services/api';

const PublishedManager = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [editObservation, setEditObservation] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');

  const fetchArticles = useCallback(async () => {
    try {
      const res = await api.getPublishedDossiers();
      setArticles(res.published || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleDelete = async (dossierId) => {
    if (!window.confirm('¿Eliminar este artículo publicado? Esta acción no se puede deshacer.')) return;
    setProcessing(dossierId);
    try {
      await api.deleteDossier(dossierId);
      
      // Optimistic update - remove immediately
      setArticles(prev => prev.filter(a => a.dossier_id !== dossierId));
      setSelectedArticle(null);
      
      // Refresh in background
      fetchArticles();
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
      fetchArticles();
    } finally {
      setProcessing(null);
    }
  };

  const handleRegenerateImage = async (dossierId, customPrompt = null, referenceImages = []) => {
    setProcessing(dossierId + '_img');
    try {
      let res;
      if (referenceImages && referenceImages.length > 0) {
        res = await api.regenerateDossierImageWithRefs(dossierId, customPrompt || null, referenceImages);
      } else {
        res = await api.regenerateDossierImage(dossierId, customPrompt || null);
      }
      
      if (res.success) {
        // Update image immediately in UI
        if (selectedArticle?.dossier_id === dossierId) {
          setSelectedArticle(prev => ({ ...prev, cover_image: res.cover_image }));
        }
        
        setImagePrompt('');
        alert('✅ Imagen regenerada correctamente');
        
        // Refresh in background
        fetchArticles();
      }
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleEditWithAI = async (dossierId) => {
    if (!editObservation.trim()) {
      alert('Escribe una observación para la IA');
      return;
    }
    setProcessing(dossierId + '_edit');
    try {
      const res = await api.editDossierWithAI(dossierId, editObservation);
      if (res.success) {
        setEditObservation('');
        alert('✅ Artículo editado correctamente');
        
        // Fetch updates in parallel
        const [_, updated] = await Promise.all([
          fetchArticles(),
          api.getDossier(dossierId)
        ]);
        
        setSelectedArticle(updated);
      }
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleSetFeatured = async (dossierId) => {
    setProcessing(dossierId + '_feat');
    try {
      await api.setDossierFeatured(dossierId);
      alert('✅ Artículo marcado como principal');
      fetchArticles(); // Non-blocking refresh
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleUnsetFeatured = async (dossierId) => {
    setProcessing(dossierId + '_feat');
    try {
      await api.unsetDossierFeatured(dossierId);
      await fetchArticles();
      alert('Artículo quitado de principal');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleSetBreaking = async (dossierId) => {
    setProcessing(dossierId + '_break');
    try {
      await api.setDossierBreaking(dossierId);
      await fetchArticles();
      alert('Marcado como última hora');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleUnsetBreaking = async (dossierId) => {
    setProcessing(dossierId + '_break');
    try {
      await api.unsetDossierBreaking(dossierId);
      await fetchArticles();
      alert('Quitado de última hora');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Artículos Publicados
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {articles.length} artículos publicados - Editar, regenerar imagen o marcar como principal
          </p>
        </div>
        <Button onClick={fetchArticles} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {articles.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay artículos publicados</p>
        </div>
      ) : (
        <div className="space-y-3">
          {articles.map((article) => (
            <ArticleCard
              key={article.dossier_id}
              article={article}
              onView={() => setSelectedArticle(article)}
              onDelete={() => handleDelete(article.dossier_id)}
              onSetFeatured={() => handleSetFeatured(article.dossier_id)}
              onUnsetFeatured={() => handleUnsetFeatured(article.dossier_id)}
              onSetBreaking={() => handleSetBreaking(article.dossier_id)}
              onUnsetBreaking={() => handleUnsetBreaking(article.dossier_id)}
              processing={processing}
            />
          ))}
        </div>
      )}

      {/* Article Edit Modal */}
      {selectedArticle && (
        <ArticleEditModal
          article={selectedArticle}
          onClose={() => { setSelectedArticle(null); setEditObservation(''); setImagePrompt(''); }}
          onDelete={() => handleDelete(selectedArticle.dossier_id)}
          onRegenerateImage={(customPrompt) => handleRegenerateImage(selectedArticle.dossier_id, customPrompt)}
          onEditWithAI={() => handleEditWithAI(selectedArticle.dossier_id)}
          onSetFeatured={() => handleSetFeatured(selectedArticle.dossier_id)}
          onUnsetFeatured={() => handleUnsetFeatured(selectedArticle.dossier_id)}
          editObservation={editObservation}
          setEditObservation={setEditObservation}
          imagePrompt={imagePrompt}
          setImagePrompt={setImagePrompt}
          processing={processing}
        />
      )}
    </div>
  );
};

const ArticleCard = ({ article, onView, onDelete, onSetFeatured, onUnsetFeatured, onSetBreaking, onUnsetBreaking, processing }) => (
  <div className={`bg-white rounded-xl border p-4 ${article.is_featured ? 'border-yellow-400 border-2' : ''}`}>
    <div className="flex gap-4">
      {/* Image preview */}
      <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden relative">
        {article.cover_image ? (
          <img src={article.cover_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-8 h-8 text-gray-300" />
          </div>
        )}
        {article.is_featured && (
          <div className="absolute top-1 left-1 bg-yellow-400 rounded-full p-1">
            <Star className="w-3 h-3 text-yellow-800" fill="currentColor" />
          </div>
        )}
        {article.is_breaking && (
          <div className="absolute top-1 right-1 bg-red-500 rounded-full p-1">
            <Zap className="w-3 h-3 text-white" fill="currentColor" />
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-xs">
                {article.sector || article.category || 'Actualidad'}
              </Badge>
              {article.is_featured && (
                <Badge className="bg-yellow-100 text-yellow-800 text-xs">Principal</Badge>
              )}
              {article.is_breaking && (
                <Badge className="bg-red-100 text-red-800 text-xs">Última Hora</Badge>
              )}
              <span className="text-xs text-gray-400">
                {article.views || 0} vistas
              </span>
            </div>
            <h3 className="font-bold text-gray-900 line-clamp-2">{article.title}</h3>
            <p className="text-sm text-gray-500 mt-1 line-clamp-1">
              {article.what_changed_today || article.executive_summary?.[0]}
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Button size="sm" variant="outline" onClick={onView}>
            <Eye className="w-4 h-4 mr-1" />
            Ver/Editar
          </Button>
          
          {article.is_featured ? (
            <Button 
              size="sm" 
              variant="outline"
              className="text-yellow-600"
              onClick={onUnsetFeatured}
              disabled={processing === article.dossier_id + '_feat'}
            >
              <Star className="w-4 h-4 mr-1" fill="currentColor" />
              Quitar Principal
            </Button>
          ) : (
            <Button 
              size="sm" 
              variant="outline"
              onClick={onSetFeatured}
              disabled={processing === article.dossier_id + '_feat'}
            >
              <Star className="w-4 h-4 mr-1" />
              Marcar Principal
            </Button>
          )}
          
          {article.is_breaking ? (
            <Button 
              size="sm" 
              variant="outline"
              className="text-red-600"
              onClick={onUnsetBreaking}
              disabled={processing === article.dossier_id + '_break'}
            >
              <Zap className="w-4 h-4 mr-1" fill="currentColor" />
              Quitar Última Hora
            </Button>
          ) : (
            <Button 
              size="sm" 
              variant="outline"
              onClick={onSetBreaking}
              disabled={processing === article.dossier_id + '_break'}
            >
              <Zap className="w-4 h-4 mr-1" />
              Última Hora
            </Button>
          )}
          
          <Button size="sm" variant="outline" className="text-red-600" onClick={onDelete}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const ArticleEditModal = ({ 
  article, onClose, onDelete, onRegenerateImage, onEditWithAI, 
  onSetFeatured, onUnsetFeatured, editObservation, setEditObservation,
  imagePrompt, setImagePrompt, processing 
}) => {
  const [showSources, setShowSources] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [referenceImages, setReferenceImages] = useState([]);
  const fileInputRef = useRef(null);
  
  const handleSuggestPrompt = async () => {
    setLoadingSuggestion(true);
    try {
      const res = await api.suggestImagePrompt(article.dossier_id);
      if (res.success && res.suggestion) {
        setImagePrompt(res.suggestion);
      }
    } catch (error) {
      console.error('Error getting suggestion:', error);
    } finally {
      setLoadingSuggestion(false);
    }
  };
  
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    setReferenceImages(prev => [...prev, ...imageFiles].slice(0, 3)); // Max 3 images
  };
  
  const removeRefImage = (index) => {
    setReferenceImages(prev => prev.filter((_, i) => i !== index));
  };
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Editar Artículo Publicado
            {article.is_featured && (
              <Badge className="bg-yellow-100 text-yellow-800">Principal</Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Cover Image */}
          <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
            {article.cover_image ? (
              <img src={article.cover_image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Image className="w-16 h-16 text-gray-300" />
              </div>
            )}
            
            {/* Image Regeneration Controls */}
            <div className="absolute bottom-2 right-2 flex flex-col items-end gap-2">
              {showImageOptions && (
                <div className="bg-white rounded-lg shadow-lg p-3 w-96">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500">
                      Instrucciones para la imagen (opcional):
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      onClick={handleSuggestPrompt}
                      disabled={loadingSuggestion}
                    >
                      {loadingSuggestion ? (
                        <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Sparkles className="w-3 h-3 mr-1" />
                      )}
                      Sugerir
                    </Button>
                  </div>
                  <Textarea
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder="Ej: Un empresario en una sala de juntas mirando gráficos financieros..."
                    rows={2}
                    className="text-sm mb-2"
                  />
                  
                  {/* Reference Images Upload */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-gray-500">Imágenes de referencia (opcional):</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="w-3 h-3 mr-1" />
                        Adjuntar
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </div>
                    {referenceImages.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {referenceImages.map((file, idx) => (
                          <div key={idx} className="relative w-16 h-16 rounded overflow-hidden border">
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                            <button
                              onClick={() => removeRefImage(idx)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {referenceImages.length === 0 && (
                      <p className="text-[10px] text-gray-400">Máx. 3 imágenes para usar como referencia visual</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm"
                      onClick={() => {
                        onRegenerateImage(imagePrompt, referenceImages);
                        setShowImageOptions(false);
                        setReferenceImages([]);
                      }}
                      disabled={processing === article.dossier_id + '_img'}
                    >
                      {processing === article.dossier_id + '_img' ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Image className="w-4 h-4 mr-1" />
                      )}
                      Generar
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setShowImageOptions(false);
                        setReferenceImages([]);
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
              <Button 
                size="sm" 
                variant="outline"
                className="bg-white"
                onClick={() => setShowImageOptions(!showImageOptions)}
                disabled={processing === article.dossier_id + '_img'}
              >
                {processing === article.dossier_id + '_img' ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Image className="w-4 h-4 mr-2" />
                )}
                Regenerar Imagen
              </Button>
            </div>
          </div>
          
          {/* Title & Meta */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge>{article.sector || article.category}</Badge>
              <span className="text-sm text-gray-500">{article.views || 0} vistas</span>
            </div>
            <h1 className="text-2xl font-bold">{article.title}</h1>
          </div>
          
          {/* Featured Controls */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Control de Portada:</span>
            {article.is_featured ? (
              <Button 
                size="sm" 
                variant="outline"
                className="text-yellow-600"
                onClick={onUnsetFeatured}
                disabled={processing === article.dossier_id + '_feat'}
              >
                <Star className="w-4 h-4 mr-1" fill="currentColor" />
                Quitar de Principal (volver a algoritmo)
              </Button>
            ) : (
              <Button 
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-600 text-white"
                onClick={onSetFeatured}
                disabled={processing === article.dossier_id + '_feat'}
              >
                <Star className="w-4 h-4 mr-1" />
                Marcar como Principal
              </Button>
            )}
          </div>
          
          {/* Content Preview */}
          <div className="prose max-w-none">
            <p className="text-lg text-gray-600 font-medium border-l-4 border-red-600 pl-4">
              {article.executive_summary?.[0] || article.content?.slice(0, 200)}
            </p>
            
            <div className="mt-6 space-y-4">
              {article.content?.split('\n\n').map((p, i) => (
                <p key={i} className="text-gray-800 leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
          
          {/* Analysis */}
          {(article.who_benefits || article.who_loses) && (
            <div className="grid md:grid-cols-2 gap-4">
              {article.who_benefits && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                  <h4 className="font-bold text-green-800 mb-2">Quién se Beneficia</h4>
                  <p className="text-green-900 text-sm">{article.who_benefits}</p>
                </div>
              )}
              {article.who_loses && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                  <h4 className="font-bold text-red-800 mb-2">Quién se Perjudica</h4>
                  <p className="text-red-900 text-sm">{article.who_loses}</p>
                </div>
              )}
            </div>
          )}
          
          {article.editorial_take && (
            <div className="p-4 bg-gray-900 text-white rounded-lg">
              <h4 className="font-bold text-yellow-400 mb-2">Nuestra Perspectiva</h4>
              <p className="text-gray-200 italic">"{article.editorial_take}"</p>
            </div>
          )}
          
          {/* Sources */}
          {article.sources?.length > 0 && (
            <div className="border-t pt-4">
              <button 
                onClick={() => setShowSources(!showSources)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {showSources ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Fuentes Consultadas ({article.sources.length})
              </button>
              {showSources && (
                <ul className="mt-2 space-y-1">
                  {article.sources.map((src, i) => (
                    <li key={i} className="text-sm">
                      <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                        {src.title || src.url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          
          {/* Edit with AI */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">Editar con IA</h4>
            <p className="text-sm text-gray-500 mb-2">
              Escribe una observación y la IA editará el artículo según tus indicaciones.
            </p>
            <Textarea
              value={editObservation}
              onChange={(e) => setEditObservation(e.target.value)}
              placeholder="Ej: Actualiza con los últimos datos, mejora el análisis de beneficiarios, hazlo más conciso..."
              rows={3}
            />
            <Button 
              className="mt-2"
              onClick={onEditWithAI}
              disabled={!editObservation.trim() || processing === article.dossier_id + '_edit'}
            >
              {processing === article.dossier_id + '_edit' ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Edit2 className="w-4 h-4 mr-2" />
              )}
              Aplicar Edición
            </Button>
          </div>
        </div>
        
        <DialogFooter className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button variant="outline" className="text-red-600" onClick={onDelete}>
            <Trash2 className="w-4 h-4 mr-2" />
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublishedManager;
